/* ----------------- Helpers --------------- */
import { isWeb } from "@helpers/platform";
import type {
	BaseItemContent,
	Media,
	OriginalItemTypes,
} from "@memorang/types";

import { useDevice } from "@hooks/useDevice";
import Box from "@memorang/ui/components/Box";
import ImagesCarousel from "@memorang/ui/components/ImagesCarousel";
import { VideoPlayer } from "@memorang/ui/components/VideoPlayer";
import { Text } from "react-native-paper";
import AudioPlayerContainer from "../containers/AudioPlayerContainer";
import composeSessionMd from "../helpers/compose-session-md";
import ItemStem from "./ItemStem";

/* ----------------- Child Components --------------- */

interface Props {
	item: BaseItemContent;
	itemId: number;
	showTTS?: boolean;
	highlightedHtml?: string;
	video?: string;
	handleStoreHtml?: (html: string) => void;
	audio?: string;
	isSummativeTest?: boolean;
	handleHighlightedTextPress?: () => void;
	originalItemType?: OriginalItemTypes;
	images?: Media[];
}
export const StemContainer = ({
	item,
	itemId,
	showTTS,
	highlightedHtml,
	video,
	handleStoreHtml,
	audio,
	isSummativeTest,
	handleHighlightedTextPress,
	images,
}: Props) => {
	const { isMobile } = useDevice();

	const stemSections: { key: string; text: string }[] = [];

	if (item.stimulus?.text) {
		stemSections.push({ key: "stimulus", text: item.stimulus?.text });
	}

	if (item.stem?.text) {
		stemSections.push({
			key: "stem",
			text: composeSessionMd({
				content: item.stem.text,
				highlights: item?.highlights,
				insertText: item?.insertSentence,
				isStem: true,
			}),
		});
	}

	const hasStemContent = stemSections.length > 0;

	const stimulusAudio = item.stimulus?.media?.find(
		(media) => media.type === "audio" || media.type === "AUDIO",
	)?.url;
	const stimulusVideo = item.stimulus?.media?.find(
		(media) => media.type === "video" || media.type === "VIDEO",
	)?.url;

	const stimulusImages = item.stimulus?.media?.filter(
		(media) => media.type === "image" || media.type === "IMAGE",
	);

	return (
		<Box
			paddingHorizontal={isWeb && !isMobile ? 0 : 16}
			gap={16}
			accessibilityLabel={`question. ${item.stem?.text}`}
		>
			{item.stimulus?.title && (
				<Text variant="titleLarge">{item.stimulus.title}</Text>
			)}
			{stimulusAudio && (
				<AudioPlayerContainer uri={stimulusAudio} itemId={itemId} />
			)}
			{stimulusImages && stimulusImages.length > 0 && (
				<ImagesCarousel
					images={stimulusImages}
					contentFit="contain"
					containerStyle={
						isSummativeTest
							? {
									justifyContent: "flex-start",
									alignItems: "flex-start",
								}
							: undefined
					}
				/>
			)}
			{stimulusVideo && <VideoPlayer videoUrl={stimulusVideo} />}
			<Box gap={16}>
				<Box>
					{images && images.length > 0 && (
						<ImagesCarousel
							images={images}
							contentFit="contain"
							containerStyle={
								isSummativeTest
									? {
											justifyContent: "flex-start",
											alignItems: "flex-start",
										}
									: undefined
							}
						/>
					)}
					{hasStemContent &&
						stemSections.map(({ key, text }) => (
							<ItemStem
								key={key}
								text={text}
								highlightedHtml={highlightedHtml}
								handleStoreHtml={handleStoreHtml}
								isSummativeTest={isSummativeTest}
								itemId={itemId}
								showTTS={showTTS}
								handleHighlightedTextPress={handleHighlightedTextPress}
							/>
						))}
				</Box>

				{audio && <AudioPlayerContainer uri={audio} itemId={itemId} />}
				{video && <VideoPlayer videoUrl={video} />}
			</Box>
		</Box>
	);
};
